/**
 * WebAccountPageViewModel - Web Platform Adapter
 * 
 * Web-specific implementation of AccountPageViewModel using React Router and browser APIs
 * Integrates with existing web authentication and navigation infrastructure
 */

import { useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { createAccountPageViewModel } from '@restaurant/viewmodels/pages/AccountPageViewModel'
import { WebAccountPageService, createWebPlatformServices } from '@restaurant/viewmodels'

/**
 * Web AccountPageViewModel Hook
 * 
 * Usage:
 * ```tsx
 * import { useWebAccountPageViewModel } from './WebAccountPageViewModel'
 * 
 * function AccountPage() {
 *   const viewModel = useWebAccountPageViewModel()
 *   const props = viewModel.getPageContentAccountProps()
 *   
 *   return <PageContentAccount {...props} />
 * }
 * ```
 */
export const useWebAccountPageViewModel = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Use the shared web platform services (navigation + platform + storage) so the
  // full IPlatformServices contract is satisfied, rather than hand-rolling a partial
  // platformService/storageService here.
  const services = useMemo(
    () => createWebPlatformServices(navigate, location),
    [navigate, location]
  )

  // Memoize account page service to prevent recreation on every render
  const accountPageService = useMemo(
    () => new WebAccountPageService(services.navigationService),
    [services.navigationService]
  )

  // Memoize the ViewModel factory to prevent recreation on every render
  const viewModelFactory = useMemo(
    () => createAccountPageViewModel(services, { accountPageService }),
    [services, accountPageService]
  )

  // Create and return ViewModel instance
  return viewModelFactory()
}

export default useWebAccountPageViewModel